// The Stats tiles' comparison group on ?live=1 (Track 2, T2-12b slice 2a), read from the nightly
// Stats files (T2-12a: one file per competition-season, `shared/src/aggregate/stats-file.ts`)
// instead of the old fixed stepping-stone group (T2-5, `/metric-distribution`).
//
// Ben's rules (docs/modules/rules.md), as they map onto the files:
// - The group starts from everyone in her competition's file and the chips narrow it. Q-010 is
//   still open, so "her league" means her league's players only (its file).
// - The group follows the competition dropdown like for like: her league → her league's file,
//   another competition → that one's, "All competitions" → her league's "All competitions" file.
// - The Season chip picks which seasons' files: untouched = her season; set = that season; removed
//   = every built season. A club season matches the calendar season that started the same year
//   (Q-006).
// - Goalkeeping compares keepers only: their minutes IN GOAL (the files' "group" rows).
// - The Position chip counts minutes AT the position: with it on, the group is the files' "group"
//   rows (one dot per position, Q-014 meanwhile B). Club, Nationality and Age read the files'
//   players + clubs; Age centres on her birth date (2b).
// - A player who changed league mid-season is one dot, in the league she finished in (Q-007): she's
//   left out of each league she left (the season's movers list, 2b). Cups keep her.
// - The bar's track = the whole database of her gender (the track file); the band = the group.
// The files go through the widget's own live answers (live/query.ts) like every call. No sharing
// between widgets: that's Track 3.

import type { OptionInfo } from "../modules/FilterPopovers.js";
import {
  DEFAULT_MINUTES_FLOOR,
  STATS_FILE_FIXED_COLUMNS,
  type StatsAllFile,
  type StatsFile,
  type StatsFileLink,
  type StatsFileLeagues,
  type StatsFileSeasons,
  type PlayerBioResponse,
  type StatsMoversFile,
  type StatsTrackFile,
} from "@catbento/shared";
import { humanizeSeasonCode } from "../modules/league-table-view.js";
import { filteredExtent, type Range } from "../modules/stats-view.js";
import { metricValueForMode } from "../engines/stats-filter-kit.js";
import {
  excludeValue,
  isExcludeValue,
  FILTER_LABELS,
  RANGE_FILTERS,
  preferredFoot,
  type ActiveFilters,
  type ExcludeValue,
  type KitMetric,
  type KitPlayer,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";
import { LiveApiError, type LiveApi } from "./api.js";
import { blankNoClaimData, claimGuardKeys, engineKeyOf, isHiddenLive } from "./stats.js";

/** What a live tile compares against: a group (its size, its band per metric, and the track),
 *  or none (numbers only, and the strip says why). Either way, the seasons the Season chip can
 *  pick (that competition's built files) and what the chip bar offers (2b). */
export type StatsGroup = (
  | {
      kind: "group";
      /** "N players match": distinct players (a player in two seasons counts once). */
      count: number;
      /** After "N players match" — e.g. " · 4 seasons" when the Season chip is removed. */
      note: string;
      /** The group's lowest → highest per tile row (the light green band). */
      band: Record<string, Range | null>;
      /** Her gender's whole database, per tile row (the bar's full length). */
      track: Record<string, Range | null>;
      /** Every group member's value per tile row, lowest first — "better than X%" on a bar's
       *  hover (Ben, 2026-10-18). */
      values: Record<string, number[]>;
    }
  | { kind: "none"; label: string }
) & { seasonOptions: SeasonOption[]; chips: ChipBarSetup };

/** What the live chip bar offers: the "+ Add filter" menu, each chip's values (from the loaded
 *  files, Ben's `getFilterOptions`), and her details a new chip starts from (Ben's
 *  `playerFilterDefault`; the Age chip centres on her birth date). */
export interface ChipBarSetup {
  addMenu: string[];
  options: Record<string, string[]>;
  refPlayer: KitPlayer;
  /** The League chip (Ben, 2026-10-17): offered only when the dropdown is her league (not a cup /
   *  All competitions); its default = that league; its list's flags + country groups. */
  leagueAllowed?: boolean;
  defaultLeague?: string;
  optionInfo?: Record<string, (value: string) => OptionInfo | undefined>;
}

export interface SeasonOption {
  id: number;
  label: string;
}

// --- loaders -------------------------------------------------------------------

/** Every league with a Stats file, and its built seasons (the League chip's list). */
export function loadFileLeagues(api: LiveApi): Promise<StatsFileLeagues> {
  return api.get<StatsFileLeagues>("/stats-file-leagues");
}

export function loadFileSeasons(api: LiveApi, competitionId: number): Promise<StatsFileSeasons> {
  return api.get<StatsFileSeasons>(`/stats-file-seasons?competitionId=${competitionId}`);
}

/** A signed link from `/stats-file`, then the file itself from S3. Null = not built (the API's
 *  404); any other failure throws (the error look + Retry). */
async function linked<T>(api: LiveApi, query: string, label: string): Promise<T | null> {
  let link: StatsFileLink;
  try {
    link = await api.get<StatsFileLink>(`/stats-file?${query}`);
  } catch (e) {
    if (e instanceof LiveApiError && e.status === 404) return null;
    throw e;
  }
  return api.download<T>(link.url, label);
}

export function loadStatsFile(api: LiveApi, competitionId: number, seasonId: number): Promise<StatsFile | null> {
  return linked<StatsFile>(api, `competitionId=${competitionId}&seasonId=${seasonId}`, `stats-file ${competitionId}/${seasonId}`);
}

export function loadAllFile(api: LiveApi, competitionId: number, seasonId: number): Promise<StatsAllFile | null> {
  return linked<StatsAllFile>(
    api,
    `competitionId=${competitionId}&seasonId=${seasonId}&part=all`,
    `stats-file ${competitionId}/${seasonId} all`
  );
}

export function loadTrack(api: LiveApi, womens: boolean): Promise<StatsTrackFile | null> {
  const gender = womens ? "women" : "men";
  return linked<StatsTrackFile>(api, `track=${gender}`, `stats-track ${gender}`);
}

/** Every season's file at once (one wait, not one per season). A season listed as built whose
 *  file is missing would be a broken listing — thrown, never a silently smaller group. */
export async function loadFiles<T>(
  seasons: number[],
  load: (seasonId: number) => Promise<T | null>
): Promise<T[]> {
  const files = await Promise.all(seasons.map(load));
  return files.map((f, i) => {
    if (f === null) throw new Error(`Season ${seasons[i]} is listed as built, but its file isn't there`);
    return f;
  });
}

// --- the Season chip -----------------------------------------------------------

/**
 * The Season chip's stored state (the tile's `_statsSeasonChip`):
 * - undefined: untouched — shown quieter, follows her season (Ben's default chip).
 * - a season id ("52024"): set by the user — stays put when her season changes.
 * - { exclude }: every season but that one.
 * - null: removed — every season.
 */
export type SeasonChip = string | ExcludeValue | null | undefined;

/** Ben, Q-006: a club season matches the calendar season that started the same year (25/26 ↔ 2025). */
export function counterpartSeason(id: number): number {
  return id > 10_000 ? id - 50_000 : id + 50_000;
}

/** A season in this competition's list — itself, or its club / calendar counterpart. */
function resolveIn(built: number[], id: number): number | null {
  if (built.includes(id)) return id;
  const other = counterpartSeason(id);
  return built.includes(other) ? other : null;
}

/** The seasons the group is made of. `asked` = the one season wanted when it has no file (not
 *  built yet), for the strip; null otherwise. Pure. */
export function groupSeasons(chip: SeasonChip, herSeason: number, built: number[]): { seasons: number[]; asked: number | null } {
  if (chip === null) return { seasons: [...built], asked: null };
  if (isExcludeValue(chip)) {
    const out = resolveIn(built, Number(chip.value));
    return { seasons: built.filter((s) => s !== out), asked: null };
  }
  const want = chip === undefined ? herSeason : Number(chip);
  const s = resolveIn(built, want);
  return s === null ? { seasons: [], asked: want } : { seasons: [s], asked: null };
}

export function seasonLabel(id: number): string {
  return humanizeSeasonCode(String(id));
}

/** What the chip shows for its state (null = no chip). */
export function seasonChipValue(chip: SeasonChip, herSeason: number): string | ExcludeValue | null {
  if (chip === null) return null;
  if (isExcludeValue(chip)) return excludeValue(seasonLabel(Number(chip.value)));
  return seasonLabel(chip === undefined ? herSeason : Number(chip));
}

/** The picker's value back to the stored state. Labels are unique per season id ("25/26",
 *  "2025"), so the label maps back to its id. Pure. */
export function seasonChipFromValue(value: string | ExcludeValue, options: SeasonOption[]): SeasonChip {
  const label = isExcludeValue(value) ? value.value : value;
  const option = options.find((o) => o.label === label);
  if (!option) throw new Error(`Season "${label}" isn't one of the chip's options`);
  return isExcludeValue(value) ? excludeValue(String(option.id)) : String(option.id);
}

// --- the Minutes chip (rules.md: no floor by default; added, it starts at 150+) ---

export const MINUTES_DOMAIN = { min: 0, max: 6000 };

export const MINUTES_CHIP_DEFAULT = { min: DEFAULT_MINUTES_FLOOR, max: MINUTES_DOMAIN.max };

/** The live Stats tile's range chips: Minutes (absolute — "150+ min" means the same for every
 *  player) and Age (Ben's own spec: relative to her, so it re-centres on a new player). */
export const LIVE_RANGE_FILTERS: RangeFilterConfig = {
  minutes: {
    label: "Minutes",
    chipLabel: "Minutes",
    unit: " min",
    relative: false,
    domain: MINUTES_DOMAIN,
    getValue: (p) => p.stats?.minutes ?? null,
    formatReadout: (r) => (r.max >= MINUTES_DOMAIN.max ? `${r.min}+ min` : `${r.min}–${r.max} min`),
  },
  age: RANGE_FILTERS.age!,
};

// --- 2b: the other chips ---------------------------------------------------------

/** The live "+ Add filter" menu, in Ben's order (`FilterChipBar` ADD_MENU_KEYS) with Season first
 *  and Minutes last. League waits for Q-010. Foot, Height and Weight show only when the data has
 *  them (Q-013: `chipsWithData`). */
export const LIVE_CHIP_KEYS = ["season", "position", "club", "nationality", "foot", "age", "height", "weight", "minutes"] as const;

// --- 2c: a chip shows only when there's enough data (Ben, Q-013) -------------------

/** The share of players (with minutes) who must have a field for its chip to show. One setting,
 *  easy to change (Ben, 2026-10-15). */
export const CHIP_DATA_THRESHOLD = 0.5;

/** What Preferred Foot is worked out from (stats-filter-kit `preferredFoot`). */
const FOOT_KEYS = ["left_foot_perc", "passes_90"] as const;

/** Does this player have the field a chip filters on? Chips not listed always have data
 *  (Position, League, Minutes, Season). */
const HAS_FIELD: Record<string, (r: GroupRow) => boolean> = {
  club: (r) => r.team != null,
  nationality: (r) => r.country != null,
  age: (r) => r.dob != null,
  height: (r) => r.height != null,
  weight: (r) => r.weight != null,
  foot: (r) => preferredFoot(r) !== null,
};

/**
 * `keys` minus the chips whose field fewer than `CHIP_DATA_THRESHOLD` of the players with minutes
 * have, in these files (one league = one gender, so it's per gender). Checked from the data, not
 * a hand-kept list: when height data arrives, the Height chip comes back by itself (Q-013). Pure.
 */
export function chipsWithData<K extends string>(keys: readonly K[], files: readonly (StatsFile | StatsAllFile)[]): K[] {
  const rows = files.flatMap((f) => {
    const total = groupRowsOf(f, { kind: "total" }, []);
    return total.length > 0 ? total : groupRowsOf(f, { kind: "all" }, []);
  });
  const played = rows.filter((r) => (r.stats.minutes ?? 0) > 0);
  return keys.filter((k) => {
    const has = HAS_FIELD[k];
    if (!has) return true;
    if (played.length === 0) return false;
    return played.filter(has).length / played.length >= CHIP_DATA_THRESHOLD;
  });
}

/** Chips that need the players' details (club, country, birth date) or the position rows — which
 *  an "All competitions" file from before 2b (v1) doesn't have. */
const DETAIL_CHIPS = ["position", "club", "nationality", "age"] as const;

/** Has this file the players' details + position rows? A competition's own file always has; an
 *  "All competitions" file from 2b on (v2). Read from the file, never guessed. */
export function hasDetails(file: StatsFile | StatsAllFile): boolean {
  return "players" in file;
}

/**
 * The add menu for this tile. Goalkeeping: no Position chip (its group is keepers in goal —
 * Ben's exception, rules.md). Old-format "All competitions" files: none of the detail chips (F1,
 * decided 2026-10-15). Her birth date unknown: no Age chip (2b decision). Pure.
 */
export function liveAddMenu(o: {
  goalkeeping: boolean;
  details: boolean;
  herDob: string | null;
  /** The chips with enough data (`chipsWithData`). */
  withData: readonly string[];
}): string[] {
  return LIVE_CHIP_KEYS.filter((k) => {
    if (!o.withData.includes(k)) return false;
    if (k === "position" && o.goalkeeping) return false;
    if ((DETAIL_CHIPS as readonly string[]).includes(k) && !o.details) return false;
    if (k === "age" && o.herDob === null) return false;
    return true;
  });
}

/**
 * Why a group can't be shown with these chips, or null. A detail chip already on (chips stay
 * when the player or competition changes) over an old-format file: it needs the rebuild (F1). An
 * Age chip on a player with no birth date: nothing to centre on (F4). Both say so rather than
 * show "0 players match", which would look like an answer. Pure.
 */
export function chipBlocker(filters: ActiveFilters, o: { details: boolean; herDob: string | null }): string | null {
  const on = (k: string) => filters[k] !== undefined;
  const needing = DETAIL_CHIPS.filter(on);
  if (needing.length > 0 && !o.details) {
    const names = needing.map((k) => (k === "age" ? "Age" : FILTER_LABELS[k])).join(", ");
    return `No comparison yet: ${names} on All competitions needs the new files (after the rebuild)`;
  }
  if (on("age") && o.herDob === null) return "No comparison: no birth date on record for this player. Remove the Age chip to compare";
  return null;
}

/** Her club in the season shown: her own row's club in that season's file; null if she isn't in
 *  it (the caller then uses her current club — F3). Pure. */
export function clubIn(files: readonly (StatsFile | StatsAllFile)[], playerId: number, seasonId: number): string | null {
  for (const f of files) {
    if (f.seasonId !== seasonId || !hasDetails(f)) continue;
    const teams = (f as { teams: Record<string, string> }).teams;
    const row = f.rows.find((r) => r[0] === playerId && (r[1] === "total" || r[1] === "all"));
    if (row && row[3] !== null) return teams[String(row[3])] ?? null;
  }
  return null;
}

/** Her birth date + country (the Age chip's centre, the Nationality chip's first value), from her
 *  bio — always there, unlike her row in a file, which is missing when the Season chip points at
 *  a season she didn't play (F2, decided 2026-10-15). */
export async function loadPlayerDetails(api: LiveApi, playerId: number): Promise<{ dob: string | null; country: string | null }> {
  const { player } = await api.get<PlayerBioResponse>(`/player?playerId=${playerId}`);
  return { dob: player.dob, country: player.country };
}

// --- the movers (Q-007: one dot, in the league she finished the season in) -----------

export function loadMovers(api: LiveApi, seasonId: number, womens: boolean): Promise<StatsMoversFile | null> {
  const gender = womens ? "women" : "men";
  return linked<StatsMoversFile>(api, `seasonId=${seasonId}&movers=${gender}`, `stats-movers ${seasonId}/${gender}`);
}

/** The players to leave out of this competition's group (its own file or its "All competitions"
 *  file): those who LEFT it mid-season for another league. No movers list (a season not rebuilt
 *  since 2b) → nobody is left out, as before. Pure. */
export function leftOut(movers: StatsMoversFile | null, competitionId: number): Set<number> {
  return new Set((movers?.movers ?? []).filter((m) => m.left.includes(competitionId)).map((m) => m.playerId));
}

// --- file rows → the shape Ben's chip code reads ------------------------------

/** One dot in the group: a KitPlayer (what `matchesFilters` reads) + who and which season. Its
 *  `stats` hold the tile's rows under the snapshot's keys, + `minutes` and `nineties_played`. */
export interface GroupRow extends KitPlayer {
  playerId: number;
  seasonId: number;
  stats: Record<string, number | null>;
}

/** Which of a file's rows make the group. */
export type RowPick =
  | { kind: "total" }
  /** Minutes at one position group only (Goalkeeping: "goalkeeper"), or at every position group
   *  (no `positionGroup`: the Position chip — one dot per position, Q-014 meanwhile B). In an
   *  "All competitions" file (v2), her whole season at that position. */
  | { kind: "group"; positionGroup?: string }
  /** "All competitions" rows; `only` = just these players (Goalkeeping: the season's keepers). */
  | { kind: "all"; only?: ReadonlySet<number> };

const NINETIES = "nineties_played";

/** A file's rows as group rows, under the snapshot's metric keys. A key the file lacks is an
 *  engine mismatch — thrown, never shown as an empty bar (the soccer-aggregates gotcha). Pure. */
export function groupRowsOf(file: StatsFile | StatsAllFile, pick: RowPick, snapshotKeys: string[]): GroupRow[] {
  const first = STATS_FILE_FIXED_COLUMNS.length; // metric values start after the fixed columns
  const column = new Map(file.metricKeys.map((k, i) => [k, first + i] as const));
  // CCAX rides along with Claimable Claim Success % so "no claim data" still shows "—" (never 0%).
  const keys = [...new Set([...snapshotKeys, ...claimGuardKeys(snapshotKeys), NINETIES])];
  for (const k of keys) {
    if (!isHiddenLive(k) && !column.has(engineKeyOf(k))) {
      throw new Error(`Stats file ${file.competitionId}/${file.seasonId} has no "${engineKeyOf(k)}" column`);
    }
  }
  // Every row carries what Preferred Foot is worked out from (Q-013: >50% of passes left-footed
  // = left), so the Foot chip filters and lists values in every widget. Only where the file has them.
  for (const k of FOOT_KEYS) if (column.has(engineKeyOf(k)) && !keys.includes(k)) keys.push(k);
  // Old-format (v1) "All competitions" files carry no players / clubs (see hasDetails).
  const players = "players" in file ? file.players : undefined;
  const teams = "teams" in file ? file.teams : undefined;
  const out: GroupRow[] = [];
  for (const row of file.rows) {
    const [playerId, kind, positionGroup, teamId, minutes] = row;
    if (kind !== pick.kind) continue;
    if (pick.kind === "group" && pick.positionGroup !== undefined && positionGroup !== pick.positionGroup) continue;
    if (pick.kind === "all" && pick.only && !pick.only.has(playerId)) continue;
    const stats: Record<string, number | null> = {};
    for (const k of keys) stats[k] = isHiddenLive(k) ? null : (row[column.get(engineKeyOf(k))!] as number | null);
    blankNoClaimData(stats);
    stats.minutes = minutes; // the fixed column: her minutes in this row (at the position, for a group row)
    const who = players?.[String(playerId)];
    out.push({
      playerId,
      seasonId: file.seasonId,
      positionTemplate: positionGroup,
      team: teamId === null ? null : (teams?.[String(teamId)] ?? null),
      country: who?.country ?? null,
      dob: who?.dob ?? null,
      stats,
    });
  }
  return out;
}

/** The season's keepers: players with minutes in goal (a "group" row for goalkeeper). */
export function keepersOf(file: StatsFile): Set<number> {
  return new Set(file.rows.filter((r) => r[1] === "group" && r[2] === "goalkeeper").map((r) => r[0]));
}

/** "N players match" — distinct players (removed Season chip: one player, many seasons). */
export function countPlayers(rows: readonly GroupRow[]): number {
  return new Set(rows.map((r) => r.playerId)).size;
}

// --- the bar ----------------------------------------------------------------------

/**
 * Which stored metric a tile row compares on in the track. Per 90 = the row's own key (the
 * engine's name). Total = the base count (`goals` for `goals_90`): her Total is per 90 × 90s
 * played, which is her base count. Pure.
 */
export function groupMetricKey(snapshotKey: string, mode: "per90" | "total"): string {
  const key = engineKeyOf(snapshotKey);
  return mode === "total" && snapshotKey.endsWith("_90") ? key.slice(0, -"_90".length) : key;
}

/** The band: the group's lowest → highest per row, on the scale shown (Total = per 90 × 90s, as
 *  her own number). Null = nobody in the group has a value. Pure. */
/** Each row's values across the group, lowest first (no value → left out). Pure. */
export function groupValues(rows: readonly GroupRow[], metrics: KitMetric[], mode: "per90" | "total"): Record<string, number[]> {
  return Object.fromEntries(
    metrics.map((m) => {
      if (isHiddenLive(m.key)) return [m.key, []];
      const vs: number[] = [];
      for (const r of rows) {
        const v = metricValueForMode(r.stats ?? {}, m, mode);
        if (v !== null && v !== undefined && !Number.isNaN(v)) vs.push(v);
      }
      return [m.key, vs.sort((a, b) => a - b)];
    }),
  );
}

/** The value at fraction `q` (0–1) of a sorted list, straight-line between neighbours. Pure. */
export function quantile(sorted: readonly number[], q: number): number {
  if (sorted.length === 0) throw new Error("quantile of nothing");
  const i = (sorted.length - 1) * q;
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (i - lo);
}

/**
 * The track: each row's 5th → 95th percentile (Ben, 2026-10-18 — as Catalyst's HOPS curve), so a
 * few extreme players (often tiny minutes) don't squash everyone else into one end of the bar. A
 * dot or band beyond an end sits at that end. A row whose middle 90% is one value keeps its full
 * lowest → highest. Null = no values. Pure.
 */
export function trackRangesOf(rows: readonly GroupRow[], metrics: KitMetric[], mode: "per90" | "total"): Record<string, Range | null> {
  const values = groupValues(rows, metrics, mode);
  return Object.fromEntries(
    metrics.map((m) => {
      const vs = values[m.key]!;
      if (isHiddenLive(m.key) || vs.length === 0) return [m.key, null];
      const lo = quantile(vs, 0.05);
      const hi = quantile(vs, 0.95);
      return [m.key, hi > lo ? { min: lo, max: hi } : { min: vs[0]!, max: vs[vs.length - 1]! }];
    }),
  );
}

export function bandRanges(rows: readonly GroupRow[], metrics: KitMetric[], mode: "per90" | "total"): Record<string, Range | null> {
  return Object.fromEntries(metrics.map((m) => [m.key, isHiddenLive(m.key) ? null : filteredExtent(rows, m, mode)]));
}

/** The track: her gender's whole database per row. Null = no track (not built) or no value. */
export function trackRanges(
  track: StatsTrackFile | null,
  metrics: KitMetric[],
  mode: "per90" | "total"
): Record<string, Range | null> {
  return Object.fromEntries(
    metrics.map((m) => {
      const r = track && !isHiddenLive(m.key) ? track.ranges[groupMetricKey(m.key, mode)] : undefined;
      return [m.key, r ? { min: r[0], max: r[1] } : null];
    })
  );
}
